import React from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { includes, eq, truncate } from 'lodash';
import { str as Crc32 } from 'crc-32';
import Chart from './chart';
import removeHtml from '../../util/remove_html_markup';

export default function Measure({
  measureData: {
    handsontable,
    highChartSetting,
    advancedMetricTargetUnit,
    embed,
    measureResults,
    singleTargetAggregationFunction,
    singleTargetMilestoneDate,
    singleTargetTargetAmount,
    advancedMetricChartType = 'column',
    label,
    viewType,
    performanceGraphUrl,
    metricType,
    summaryDetails,
    advancedMetric,
    advancedMetricType,
    measureId,
    visualTitle,
    visualDescription,
  },
  startOfFinancialYear,
  linkPrefix,
  columns,
}) {
  let titleStyles = {};
  let descriptionStyles = {};

  if (includes(['Handsontable', 'Highcharts'], metricType)) {
    let jsonString = embed;

    if (eq('Highcharts', metricType)) {
      jsonString = highChartSetting;
    } else if (eq('Handsontable', metricType)) {
      jsonString = handsontable;
    }

    const {
      title: { font_size: titleFontSize, color: titleColor } = {},
      subtitle: { font_size: descriptionFontSize, color: descriptionColor } = {},
    } = JSON.parse(jsonString);

    titleStyles = { fontSize: titleFontSize, color: titleColor };

    descriptionStyles = { fontSize: descriptionFontSize, color: descriptionColor };
  }

  const chart = (
    <div className="chart-container" style={metricType === 'Summary' ? { overflow: 'auto' } : {}} aria-label={label}>
      <Chart
        height={300}
        advancedMetricTargetUnit={advancedMetricTargetUnit}
        measureResults={measureResults}
        singleTargetAggregationFunction={singleTargetAggregationFunction}
        singleTargetMilestoneDate={singleTargetMilestoneDate}
        singleTargetTargetAmount={singleTargetTargetAmount}
        advancedMetricChartType={advancedMetricChartType}
        showCSVDownload
        label={label}
        startOfFinancialYear={startOfFinancialYear}
        viewType={viewType || 'daily'}
        handsontable={handsontable}
        highChartSetting={highChartSetting}
        performanceGraphUrl={performanceGraphUrl}
        embed={embed}
        metricType={metricType}
        summaryDetails={summaryDetails}
        advancedMetric={advancedMetric}
        advancedMetricType={advancedMetricType}
      />
    </div>
  );
  let milestoneLegend;

  if (singleTargetMilestoneDate) {
    milestoneLegend = (
      <li>
        <span className="milestone-legend-symbol" />
        <strong>Milestone:</strong>
        {Moment(singleTargetMilestoneDate).format('ll')}
      </li>
    );
  }

  let targetAmountLegend;

  if (singleTargetTargetAmount) {
    targetAmountLegend = (
      <li>
        <span className="target-legend-symbol" />
        <strong>Target:</strong>
        {singleTargetTargetAmount}
      </li>
    );
  }

  let additionalHighchartsLegends;

  if (includes(['OriginalSingle', 'OriginalMulti'], metricType) || eq(advancedMetric, 'Yes')) {
    additionalHighchartsLegends = (
      <ul className="additional-highcharts-legends">
        {targetAmountLegend}
        {milestoneLegend}
      </ul>
    );
  }

  return (
    <article className={`measure column-row-${columns} Measure-${Math.abs(Crc32(measureId))}`}>
      <div className="measure-inner" id={`measure-inner-${measureId}`}>
        <Link to={`${linkPrefix}/${measureId}`} className="measure-expand-button" aria-label="Expand">
          <span className="measure-expand-container">
            <FontAwesomeIcon icon={['fal', 'expand-alt']} />
          </span>
        </Link>
        <span className="card-visual-title" style={titleStyles}>
          {visualTitle}
        </span>
        {chart}
        <span className="card-visual-description pd-custom fr-view" style={descriptionStyles}>
          {truncate(removeHtml(visualDescription), {
            separator: ' ',
            length: 108,
          })}
        </span>
        {additionalHighchartsLegends}
      </div>
    </article>
  );
}

Measure.propTypes = {
  measureData: PropTypes.shape({
    handsontable: PropTypes.string,
    highChartSetting: PropTypes.string,
    advancedMetricTargetUnit: PropTypes.string,
    measureResults: PropTypes.arrayOf(PropTypes.shape({})),
    singleTargetAggregationFunction: PropTypes.string,
    singleTargetMilestoneDate: PropTypes.string,
    singleTargetTargetAmount: PropTypes.string,
    advancedMetricChartType: PropTypes.string,
    label: PropTypes.string,
    viewType: PropTypes.string,
    performanceGraphUrl: PropTypes.string,
    embed: PropTypes.string,
    metricType: PropTypes.string,
    summaryDetails: PropTypes.string,
    advancedMetric: PropTypes.string,
    advancedMetricType: PropTypes.string,
    measureId: PropTypes.string,
    visualTitle: PropTypes.string,
    visualDescription: PropTypes.string,
  }).isRequired,
  linkPrefix: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
  startOfFinancialYear: PropTypes.string.isRequired,
  columns: PropTypes.oneOf([1, 2, 3]).isRequired,
};
